import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  Calendar as CalendarIcon, Clock, Search, CheckCircle2, X, RotateCcw, UserCheck,
  MessageSquare, Filter, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useRealtime } from "@/hooks/useRealtime";

const STATUS_META = {
  requested:   { label: "Requested",   badge: "bg-amber-100 text-amber-800",     dot: "bg-amber-500" },
  approved:    { label: "Confirmed",   badge: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  rescheduled: { label: "Rescheduled", badge: "bg-sky-100 text-sky-700",         dot: "bg-sky-500" },
  completed:   { label: "Completed",   badge: "bg-slate-200 text-slate-700",     dot: "bg-slate-500" },
  cancelled:   { label: "Cancelled",   badge: "bg-red-100 text-red-700",         dot: "bg-red-500" },
  no_show:     { label: "No-show",     badge: "bg-orange-100 text-orange-700",   dot: "bg-orange-500" },
};

export default function AdminMeetings() {
  const [meta, setMeta] = useState({ reasons: [], statuses: [] });
  const [meetings, setMeetings] = useState([]);
  const [team, setTeam] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [reason, setReason] = useState("all");
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (reason !== "all") params.reason = reason;
      if (q) params.q = q;
      if (date) params.date = date;
      const { data } = await api.get("/admin/meetings", { params });
      setMeetings(data);
      const s = await api.get("/admin/meetings/stats");
      setStats(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/meetings/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/admin/team-members").then(({ data }) => setTeam(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, reason, date]);

  // Live updates: refresh when a meeting is requested / updated
  useRealtime((evt) => {
    if (evt?.type === "notification" && evt.data?.kind?.startsWith("meeting_")) {
      load();
    }
  });

  const filtered = useMemo(() => meetings, [meetings]);

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Admin console</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900">Meetings</h1>
          <p className="text-slate-500 mt-1">Every scheduled call across your entire book.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatBlock icon={AlertCircle} label="Awaiting approval" value={stats.requested_count || 0} color="text-amber-600" />
        <StatBlock icon={CalendarIcon} label="Today" value={stats.today_count || 0} color="text-sky-600" />
        <StatBlock icon={CheckCircle2} label="Upcoming (confirmed)" value={stats.upcoming_count || 0} color="text-emerald-600" />
        <StatBlock icon={Clock} label="All meetings" value={meetings.length} color="text-slate-700" />
      </div>

      <Card className="p-4 sm:p-6 rounded-2xl border-slate-100 mb-6">
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search client, reason…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-10 h-10 rounded-xl" data-testid="admin-meetings-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44 h-10 rounded-xl" data-testid="admin-meetings-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {(meta.statuses || []).map((s) => <SelectItem key={s} value={s} data-testid={`admin-status-opt-${s}`}>{STATUS_META[s]?.label || s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="w-52 h-10 rounded-xl" data-testid="admin-meetings-reason"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reasons</SelectItem>
              {(meta.reasons || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 h-10 rounded-xl" data-testid="admin-meetings-date" />
          {(status !== "all" || reason !== "all" || date || q) && (
            <Button variant="ghost" onClick={() => { setStatus("all"); setReason("all"); setDate(""); setQ(""); }} className="h-10" data-testid="admin-meetings-clear">
              <Filter size={14} className="mr-1" /> Clear
            </Button>
          )}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Loading…</TableCell></TableRow>}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8" data-testid="admin-meetings-empty">No meetings match your filters yet.</TableCell></TableRow>
              )}
              {!loading && filtered.map((m) => {
                const s = STATUS_META[m.status] || STATUS_META.requested;
                const dt = new Date(m.scheduled_at);
                return (
                  <TableRow key={m.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(m)} data-testid={`admin-meeting-row-${m.id}`}>
                    <TableCell className="whitespace-nowrap">
                      <p className="font-semibold text-slate-900">{dt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p>
                      <p className="text-xs text-slate-500">{dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</p>
                    </TableCell>
                    <TableCell>
                      <p className="font-semibold text-slate-800">{m.client_name || "—"}</p>
                      <p className="text-xs text-slate-500">{m.client_email}</p>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{m.reason}</TableCell>
                    <TableCell>
                      <Badge className={`${s.badge} rounded-full font-semibold gap-1`}>
                        <span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{m.assigned_team_member_name || <span className="text-slate-400">Unassigned</span>}</TableCell>
                    <TableCell className="text-sm text-slate-500 max-w-[180px] truncate">{m.client_notes || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelected(m); }} data-testid={`admin-meeting-manage-${m.id}`}>
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <ManageMeetingDialog
        meeting={selected}
        team={team}
        reasons={meta.reasons || []}
        statuses={meta.statuses || []}
        onClose={() => setSelected(null)}
        onSaved={() => { setSelected(null); load(); }}
      />
    </AppShell>
  );
}

function StatBlock({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-5 rounded-2xl border-slate-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">{label}</p>
          <p className={`font-display text-3xl font-black mt-1 ${color}`}>{value}</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-2 text-slate-500"><Icon size={18} /></div>
      </div>
    </Card>
  );
}

function ManageMeetingDialog({ meeting, team, reasons, statuses, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (meeting) {
      setForm({
        reason: meeting.reason,
        status: meeting.status,
        scheduled_at: meeting.scheduled_at?.slice(0, 16), // yyyy-MM-ddTHH:mm
        assigned_team_member_id: meeting.assigned_team_member_id || "none",
        duration_minutes: meeting.duration_minutes || 30,
      });
      setNote("");
    }
  }, [meeting]);

  if (!meeting) return null;

  const patch = async (patchBody) => {
    setBusy(true);
    try {
      await api.put(`/admin/meetings/${meeting.id}`, patchBody);
      toast.success("Meeting updated — the client has been notified.");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    const body = {
      reason: form.reason,
      status: form.status,
      duration_minutes: Number(form.duration_minutes) || 30,
      assigned_team_member_id: form.assigned_team_member_id === "none" ? null : form.assigned_team_member_id,
    };
    // Only submit scheduled_at if it changed and is a valid datetime-local
    if (form.scheduled_at && form.scheduled_at !== meeting.scheduled_at?.slice(0, 16)) {
      // Convert local datetime input to ISO
      body.scheduled_at = new Date(form.scheduled_at).toISOString();
    }
    await patch(body);
  };

  const quickAction = async (action) => {
    if (action === "approve") return patch({ status: "approved" });
    if (action === "complete") return patch({ status: "completed" });
    if (action === "no_show") return patch({ status: "no_show" });
    if (action === "cancel") {
      if (!window.confirm("Cancel this meeting and notify the client?")) return;
      return patch({ status: "cancelled" });
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/meetings/${meeting.id}/notes`, { text: note });
      toast.success("Note added");
      setNote("");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!meeting} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manage-meeting-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Manage meeting</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="rounded-2xl bg-slate-50 p-4 flex items-start gap-3">
            <UserCheck size={18} className="text-amber-500 mt-1" />
            <div>
              <p className="font-semibold text-slate-900">{meeting.client_name || "—"}</p>
              <p className="text-xs text-slate-500">{meeting.client_email} · {meeting.client_phone || "no phone"}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Reason</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger data-testid="manage-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="manage-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => <SelectItem key={s} value={s} data-testid={`manage-status-${s}`}>{STATUS_META[s]?.label || s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>When (local time)</Label>
              <Input type="datetime-local" value={form.scheduled_at || ""} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="manage-when" />
            </div>
            <div>
              <Label>Duration (min)</Label>
              <Input type="number" min={15} max={120} step={15} value={form.duration_minutes || 30} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="manage-duration" />
            </div>
            <div className="col-span-2">
              <Label>Assign to team member</Label>
              <Select value={form.assigned_team_member_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_team_member_id: v })}>
                <SelectTrigger data-testid="manage-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => quickAction("approve")} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600" data-testid="quick-approve">
              <CheckCircle2 size={14} className="mr-1" /> Approve
            </Button>
            <Button size="sm" onClick={() => quickAction("complete")} disabled={busy} variant="outline" data-testid="quick-complete">
              <CheckCircle2 size={14} className="mr-1" /> Completed
            </Button>
            <Button size="sm" onClick={() => quickAction("no_show")} disabled={busy} variant="outline" data-testid="quick-no-show">
              <AlertCircle size={14} className="mr-1" /> No-show
            </Button>
            <Button size="sm" onClick={() => quickAction("cancel")} disabled={busy} variant="ghost" className="text-red-500" data-testid="quick-cancel">
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <div className="flex-1" />
            <Button size="sm" onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="manage-save">
              <RotateCcw size={14} className="mr-1" /> {busy ? "Saving…" : "Save changes"}
            </Button>
          </div>

          {meeting.client_notes && (
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm">
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-600 mb-1">Client wrote</p>
              <p className="text-slate-700 italic">&ldquo;{meeting.client_notes}&rdquo;</p>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Internal notes</Label>
            <div className="space-y-2 mt-2 max-h-60 overflow-y-auto pr-1" data-testid="internal-notes-list">
              {(meeting.internal_notes || []).length === 0 && <p className="text-xs text-slate-400 py-2">No internal notes yet.</p>}
              {(meeting.internal_notes || []).map((n) => (
                <div key={n.id} className="rounded-lg bg-slate-50 p-3 text-sm" data-testid={`internal-note-${n.id}`}>
                  <p className="text-slate-800">{n.text}</p>
                  <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-bold">{n.author_name} · {new Date(n.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note (client can't see this)…" data-testid="internal-note-input" />
              <Button onClick={addNote} disabled={busy || !note.trim()} className="self-end" data-testid="internal-note-add">
                <MessageSquare size={14} className="mr-1" /> Add
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
